import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import StatementReport from "./StatementReport";
import LinkStatementForm from "./LinkStatementForm";
import { can, formatSize, PERMISSIONS } from "./glUtils";

const DEFAULT_MAX_KB = 10 * 1024;
const PENDING = ["uploaded", "parsing"];
// Must stay in line with ParseStatementJob::STALE_AFTER_SECONDS for the "parsing" case.
const STALLED_AFTER_MS = { uploaded: 2 * 60 * 1000, parsing: 15 * 60 * 1000 };

function stageLabel(statement) {
  switch (statement.stage) {
    case "reading":
      return "Reading the PDF…";
    case "parsing":
      return "Reading every row of the statement…";
    case "saving": {
      const rows = statement.parsed_rows ? `${statement.parsed_rows} rows` : "rows";
      return Number.isFinite(statement.progress)
        ? `Saving ${rows} in batches… ${statement.progress}%`
        : `Saving ${rows} in batches…`;
    }
    default:
      return "Queued — waiting for the background worker…";
  }
}

function isStalled(statement) {
  const since = Date.parse(statement.updated_at || statement.created_at || "");
  const limit = STALLED_AFTER_MS[statement.status];
  return Boolean(limit) && Number.isFinite(since) && Date.now() - since > limit;
}

export default function UploadStatementModal({ isOpen, toggle, meta, initialStatementId = null }) {
  const queryClient = useQueryClient();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null);
  const [statementId, setStatementId] = useState(initialStatementId);
  const maxBytes = (Number(meta?.max_statement_kb) || DEFAULT_MAX_KB) * 1024;
  const maxLabel = formatSize(maxBytes);

  const statementQuery = useQuery({
    queryKey: ["glStatement", statementId],
    queryFn: () => GeneralLedgerService.getStatement(statementId).then((r) => r.data),
    enabled: Boolean(statementId) && isOpen,
    refetchInterval: (query) =>
      query.state.status !== "error" && PENDING.includes(query.state.data?.status) ? 2000 : false,
  });

  const statement = statementQuery.data;

  const uploadMutation = useMutation({
    mutationFn: (file) => GeneralLedgerService.uploadStatement(file, setProgress),
    onSuccess: (res) => {
      setStatementId(res.data.id);
      queryClient.invalidateQueries({ queryKey: ["glStatements"] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Upload failed.")),
    onSettled: () => setProgress(null),
  });

  const retryMutation = useMutation({
    mutationFn: () => GeneralLedgerService.reparseStatement(statementId),
    onSuccess: () => {
      toast.success("Statement queued for processing again.");
      statementQuery.refetch();
      queryClient.invalidateQueries({ queryKey: ["glStatements"] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not retry processing.")),
  });

  const pickFile = (file) => {
    if (inputRef.current) inputRef.current.value = "";
    if (!file || uploadMutation.isPending) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF bank statements are accepted.");
      return;
    }
    if (file.size > maxBytes) {
      toast.error(`File is larger than ${maxLabel}.`);
      return;
    }
    uploadMutation.mutate(file);
  };

  const close = () => {
    ["glStatements", "glEntries", "glMeta"].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    setStatementId(null);
    toggle();
  };

  const needsLink = statement && !statement.bank_profile_id && ["parsed", "mismatch"].includes(statement.status);

  return (
    <Modal isOpen={isOpen} toggle={close} size="lg" backdrop="static">
      <ModalHeader toggle={close}>Upload bank statement</ModalHeader>
      <ModalBody>
        {!statementId && (
          <div
            className={`gl-dropzone ${dragging ? "dragging" : ""} ${uploadMutation.isPending ? "disabled" : ""}`}
            role="button"
            tabIndex={0}
            aria-disabled={uploadMutation.isPending}
            onClick={() => !uploadMutation.isPending && inputRef.current?.click()}
            onKeyDown={(e) => !uploadMutation.isPending && (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              pickFile(e.dataTransfer.files?.[0]);
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            <strong>Drop a PDF statement here or click to browse</strong>
            <p className="gl-muted mb-0">
              Supported: {(meta?.templates || []).map((t) => t.bank_name).join(", ") || "bank statement templates"} · max {maxLabel}
            </p>
            {progress !== null && <Progress className="mt-3" value={progress}>{progress}%</Progress>}
          </div>
        )}

        {statementId && !statement && !statementQuery.isError && <Spinner size="sm" />}
        {statementQuery.isError && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2 mb-0">
            <span>{getApiErrorMessage(statementQuery.error, "Could not load the statement status.")}</span>
            <Button size="sm" color="light" onClick={() => statementQuery.refetch()}>Try again</Button>
          </div>
        )}

        {statement && (
          <>
            <h6 className="mb-3">{statement.original_name}</h6>
            {PENDING.includes(statement.status) ? (
              <>
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" /> {stageLabel(statement)}
                </div>
                {statement.stage === "saving" && Number.isFinite(statement.progress) && (
                  <Progress className="mt-2" value={statement.progress} />
                )}
                <p className="gl-muted small mt-2 mb-0">
                  Processing runs in the background — you can close this window and check the Statements tab later.
                </p>
                {isStalled(statement) && can(meta, PERMISSIONS.UPLOAD) && (
                  <div className="alert alert-warning mt-3 mb-0 d-flex align-items-center justify-content-between gap-2">
                    <span>This is taking longer than expected.</span>
                    <Button
                      size="sm"
                      color="warning"
                      disabled={retryMutation.isPending}
                      onClick={() => retryMutation.mutate()}
                    >
                      {retryMutation.isPending ? <Spinner size="sm" /> : "Retry processing"}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <StatementReport statement={statement} />
            )}
            {statement.bank_profile && (
              <div className="alert alert-success mt-3 mb-0">
                Linked to <b>{statement.bank_profile.label}</b> ****{statement.bank_profile.account_last4}
                {statement.company_name ? ` (${statement.company_name})` : ""}.
              </div>
            )}
            {statement.status === "failed" && can(meta, PERMISSIONS.UPLOAD) && (
              <div className="d-flex justify-content-end mt-2">
                <Button size="sm" color="primary" disabled={retryMutation.isPending} onClick={() => retryMutation.mutate()}>
                  {retryMutation.isPending ? <Spinner size="sm" /> : "Retry processing"}
                </Button>
              </div>
            )}
            {statement.link_reason === "currency_mismatch" && (
              <div className="alert alert-warning mt-3 mb-0">
                A profile exists for this account but with a different currency. An admin must review it.
              </div>
            )}
            {statement.link_reason === "company_inactive" && (
              <div className="alert alert-warning mt-3 mb-0">
                This account belongs to a deactivated company, so the statement was not linked. Reactivate the company
                in the Companies tab, then re-parse this statement.
              </div>
            )}
            {statement.link_reason === "profile_inactive" && (
              <div className="alert alert-warning mt-3 mb-0">
                The bank profile for this account is deactivated, so the statement was not linked. Reactivate the profile
                in the Bank profiles tab, then re-parse this statement.
              </div>
            )}
            {needsLink && statement.link_reason !== "currency_mismatch" && can(meta, PERMISSIONS.MANAGE_PROFILES) && (
              <div className="mt-3">
                <LinkStatementForm statement={statement} meta={meta} onLinked={() => statementQuery.refetch()} />
              </div>
            )}
            {needsLink && !can(meta, PERMISSIONS.MANAGE_PROFILES) && (
              <div className="alert alert-info mt-3 mb-0">
                This account has no bank profile yet. Ask an admin to link the statement; until then its rows are
                visible to admins only and do not appear in your ledger.
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {statement && !PENDING.includes(statement.status) && (
          <Button color="light" onClick={() => setStatementId(null)}>Upload another</Button>
        )}
        <Button color="secondary" onClick={close}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}
